// ───────────────────────────────────────────────────────────────────────────
// Demo Mode
// A fully self-contained, public, fake "marketing demo" of the organization view.
// When enabled, no real network requests reach the backend: every API call made
// through `makeRequest` is short-circuited and answered with fake data instead.
//
// The flag lives in sessionStorage so it is scoped per browser tab — the demo is
// meant to be opened in its own tab from the (external) landing page, and never
// leaks into a real authenticated session.
// ───────────────────────────────────────────────────────────────────────────

export const DEMO_ORG_ID = 'demo-org-0001';
export const DEMO_USER_ID = 'demo-user-0001';
export const DEMO_FLAG_KEY = 'demoMode';

export const isDemoMode = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage.getItem(DEMO_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
};

export const enableDemoMode = (): void => {
  try {
    window.sessionStorage.setItem(DEMO_FLAG_KEY, 'true');
  } catch {
    /* ignore */
  }
};

export const disableDemoMode = (): void => {
  try {
    window.sessionStorage.removeItem(DEMO_FLAG_KEY);
  } catch {
    /* ignore */
  }
};

// Build a structurally-valid (but signature-less) JWT so anything that decodes the
// token — e.g. the auth interceptor or `refreshUserData` — keeps working offline.
// Claims are ASCII-only on purpose: btoa cannot encode non-Latin1 characters.
export const buildFakeJwt = (): string => {
  const now = Math.floor(Date.now() / 1000);
  const header = { alg: 'none', typ: 'JWT' };
  const payload = {
    sub: DEMO_USER_ID,
    username: 'demo@mohasby.com',
    name: 'Demo Organization',
    role: 'organization',
    permissions: [],
    organizationIds: [DEMO_ORG_ID],
    customPermissions: ['access_invoices', 'access_reports', 'access_chat'],
    exp: now + 60 * 60 * 24 * 365,
    iat: now,
  };
  const enc = (obj: unknown) =>
    btoa(JSON.stringify(obj)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  return `${enc(header)}.${enc(payload)}.demo`;
};
